import { useEffect, useRef, useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { tasksApi, Task } from '@/api'

const SEEN_TASKS_KEY = 'copilot-seen-task-ids'

function getSeenTaskIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SEEN_TASKS_KEY)
    if (raw) return new Set(JSON.parse(raw))
  } catch {
    // ignore parse errors
  }
  return new Set()
}

function setSeenTaskIds(ids: Set<string>) {
  sessionStorage.setItem(SEEN_TASKS_KEY, JSON.stringify([...ids]))
}

export interface NewTaskNotification {
  task: Task
  dismissed: boolean
}

/**
 * Polls for newly assigned tasks and surfaces them as notifications.
 * Deduplicates against sessionStorage so each task only notifies once per session.
 */
export function useNewTaskNotification(intervalMs: number = 30000) {
  const [newTasks, setNewTasks] = useState<NewTaskNotification[]>([])
  const seenRef = useRef<Set<string>>(getSeenTaskIds())
  const initializedRef = useRef(false)

  const { data } = useQuery({
    queryKey: ['tasks', 'notification-poll'],
    queryFn: () =>
      tasksApi.list({
        status: 'pending',
        per_page: 10,
      }),
    refetchInterval: intervalMs,
    refetchIntervalInBackground: false,
  })

  useEffect(() => {
    if (!data?.items) return

    // On first load, seed the seen set without triggering notifications
    if (!initializedRef.current) {
      const ids = new Set(seenRef.current)
      data.items.forEach((t) => ids.add(t.id))
      seenRef.current = ids
      setSeenTaskIds(ids)
      initializedRef.current = true
      return
    }

    const unseen = data.items.filter((t) => !seenRef.current.has(t.id))
    if (unseen.length === 0) return

    // Mark as seen
    const ids = new Set(seenRef.current)
    unseen.forEach((t) => ids.add(t.id))
    seenRef.current = ids
    setSeenTaskIds(ids)

    // Add to notification list
    setNewTasks((prev) => [
      ...unseen.map((task) => ({ task, dismissed: false })),
      ...prev,
    ])
  }, [data])

  const dismissTask = useCallback((taskId: string) => {
    setNewTasks((prev) =>
      prev.map((n) => (n.task.id === taskId ? { ...n, dismissed: true } : n)),
    )
  }, [])

  const dismissAll = useCallback(() => {
    setNewTasks((prev) => prev.map((n) => ({ ...n, dismissed: true })))
  }, [])

  const activeNotifications = newTasks.filter((n) => !n.dismissed)

  return { newTasks: activeNotifications, dismissTask, dismissAll }
}
